import React, { useState, useEffect, useMemo } from 'react'
import { useGetMyInfo } from 'app/gomsacademy/hooks/GetMyInfo'
import { useAcademyList } from 'app/gomsacademy/hooks/AcademyList'
import { useTeacherStudent } from 'app/gomsacademy/hooks/TeacherStudentList'
import { usePaySSAMPayment } from 'app/gomsacademy/hooks/PaySSAMPayment'
import { usePaySSAMMerchant } from 'app/gomsacademy/hooks/PaySSAMMerchant'
import * as antd from 'antd'
import styled from 'styled-components'
import { LectureWrapper, LectureView, LectureViewHeader, FixBtns } from '../Lecture/styles'

type StudentItem = { id: number; name?: string; phone?: string; phone2?: string; schoolType?: string; grade?: string; status?: number }

const Payment = () => {
	const { myInfo } = useGetMyInfo()
	const { AcademyListData } = useAcademyList()
	const { TeacherStudentData: studentList } = useTeacherStudent(3)

	// Get current branch/dept ID
	const currentBranch = AcademyListData?.find(branch => branch.name === myInfo?.branchName)
	const deptId = currentBranch?.id

	const {
		orders,
		loading,
		actionLoading,
		fetchOrders,
		sendBills,
		readBill,
		resendBill,
		cancelBill,
		destroyBill,
	} = usePaySSAMPayment(deptId)
	const {
		merchantStatus,
		loadingStatus: loadingMerchantStatus,
		statusChecked: merchantStatusChecked,
		refreshStatus,
	} = usePaySSAMMerchant(deptId)

	const [activeTab, setActiveTab] = useState<string>('bill')
	const [sendingBill, setSendingBill] = useState(false)
	const [readModal, setReadModal] = useState<{ visible: boolean; order: any; data: any }>({ visible: false, order: null, data: null })
	const [studentSearch, setStudentSearch] = useState('')
	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [billRecipientPhone, setBillRecipientPhone] = useState<'parent' | 'student'>('parent')
	const [recipientMode, setRecipientMode] = useState<'student' | 'direct'>('student')
	const [directRecipients, setDirectRecipients] = useState<Array<{ name: string; phone: string }>>([])
	const [directName, setDirectName] = useState('')
	const [directPhone, setDirectPhone] = useState('')

	// Bill form state (shared for batch)
	const [productNm, setProductNm] = useState('')
	const [amount, setAmount] = useState('')
	const [message, setMessage] = useState('')
	const [expireDt, setExpireDt] = useState(() => {
		const d = new Date()
		d.setDate(d.getDate() + 14)
		return d.toISOString().slice(0, 10)
	})
	const [billIssuer, setBillIssuer] = useState('')

	const students = useMemo(() => (Array.isArray(studentList) ? studentList : []).filter((s: StudentItem) => s.status === 1), [studentList])
	const filteredStudents = useMemo(() => {
		const kw = studentSearch.trim().toLowerCase()
		if (!kw) return students
		return students.filter((s: StudentItem) => {
			const name = String(s?.name || '').toLowerCase()
			const phone = String(s?.phone || '')
			const phone2 = String(s?.phone2 || '')
			return name.includes(kw) || phone.includes(kw) || phone2.includes(kw)
		})
	}, [students, studentSearch])

	useEffect(() => {
		if (deptId) {
			fetchOrders()
			refreshStatus(false)
		}
	}, [deptId, fetchOrders, refreshStatus])

	useEffect(() => {
		if (currentBranch?.name) {
			setBillIssuer(prev => prev || currentBranch.name)
		}
	}, [currentBranch?.name])

	const selectedCount = selectedIds.length
	const toggleAllFiltered = () => {
		const ids = filteredStudents.map((s: StudentItem) => Number(s.id)).filter((v: number) => !Number.isNaN(v))
		const allSelected = ids.length > 0 && ids.every((id: number) => selectedIds.includes(id))
		setSelectedIds(
			allSelected
				? selectedIds.filter(id => !ids.includes(id))
				: Array.from(new Set([...selectedIds, ...ids]))
		)
	}
	const toggleOne = (id: number) => {
		setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
	}

	const handleAddDirectRecipient = () => {
		const nameStr = directName.trim()
		const phoneStr = directPhone.trim().replace(/\s/g, '')
		if (!phoneStr) {
			antd.message.warning('전화번호를 입력해주세요.')
			return
		}
		setDirectRecipients(prev => [...prev, { name: nameStr || '수신자', phone: phoneStr }])
		setDirectName('')
		setDirectPhone('')
	}

	const handleRemoveDirectRecipient = (index: number) => {
		setDirectRecipients(prev => prev.filter((_, i) => i !== index))
	}

	const isMerchantApproved = Boolean(merchantStatus?.paymentAvailable || (merchantStatus?.isLinked && merchantStatus?.reviewStatus === 'O'))
	const canSendBill = merchantStatusChecked && isMerchantApproved && !loadingMerchantStatus

	const handleSendBill = async () => {
		if (!merchantStatusChecked || loadingMerchantStatus) {
			antd.message.warning('가맹점 상태를 확인 중입니다. 잠시 후 다시 시도해주세요.')
			return
		}
		if (!isMerchantApproved) {
			antd.message.error('결제선생 등록이 필요합니다.')
			return
		}
		if (!productNm.trim()) {
			antd.message.error('상품명을 입력해주세요.')
			return
		}
		const amountNum = parseInt(amount, 10)
		if (!amountNum || amountNum < 1) {
			antd.message.error('금액을 올바르게 입력해주세요.')
			return
		}

		let recipients: Array<{ customerName: string; customerPhone: string }> = []

		if (recipientMode === 'direct') {
			if (directRecipients.length === 0) {
				antd.message.error('수신자를 추가해 주세요.')
				return
			}
			recipients = directRecipients
				.map(r => ({ customerName: (r.name || '수신자').trim(), customerPhone: r.phone.trim().replace(/\s/g, '') }))
				.filter(r => r.customerPhone)
			if (recipients.length === 0) {
				antd.message.error('전화번호가 있는 수신자를 추가해 주세요.')
				return
			}
		} else {
			if (selectedIds.length === 0) {
				antd.message.error('수신할 학생을 선택해 주세요.')
				return
			}
			const studentMap = new Map(students.map((s: StudentItem) => [Number(s.id), s]))
			const missingPhone: string[] = []
			for (const id of selectedIds) {
				const s = studentMap.get(id) as StudentItem | undefined
				if (!s) continue
				const phone = billRecipientPhone === 'parent' ? (s.phone2 || s.phone) : (s.phone || s.phone2)
				const phoneStr = phone ? String(phone).trim() : ''
				if (!phoneStr) {
					missingPhone.push(s.name || `ID ${id}`)
					continue
				}
				recipients.push({
					customerName: String(s.name || '').trim() || `학생 ${id}`,
					customerPhone: phoneStr,
				})
			}
			if (missingPhone.length > 0) {
				antd.message.warning(`전화번호가 없는 수신자가 있어 제외됩니다: ${missingPhone.slice(0, 3).join(', ')}${missingPhone.length > 3 ? ' 외 ' + (missingPhone.length - 3) + '명' : ''}`)
			}
			if (recipients.length === 0) {
				antd.message.error('발송 가능한 수신자가 없습니다. 전화번호를 확인해 주세요.')
				return
			}
		}

		if (!window.confirm(`${recipients.length}명에게 청구서를 발송할까요?`)) return

		setSendingBill(true)
		try {
			await sendBills({
				productNm: productNm.trim(),
				amount: amountNum,
				message: message.trim() || undefined,
				expireDt: expireDt || undefined,
				billIssuer: billIssuer.trim() || undefined,
				recipients,
			})
			setProductNm('')
			setAmount('')
			setMessage('')
			setExpireDt(() => {
				const d = new Date()
				d.setDate(d.getDate() + 14)
				return d.toISOString().slice(0, 10)
			})
			setBillIssuer(currentBranch?.name || '')
			setSelectedIds([])
			if (recipientMode === 'direct') {
				setDirectRecipients([])
			}
		} catch (err) {
			// Error already handled in hook
		} finally {
			setSendingBill(false)
		}
	}

	const handleReadBill = async (order: any) => {
		try {
			const data = await readBill(order.billId)
			setReadModal({ visible: true, order, data })
		} catch (err) {
			// Error already handled in hook
		}
	}

	const handleCopyUrl = (url: string) => {
		navigator.clipboard.writeText(url).then(() => {
			antd.message.success('링크가 복사되었습니다.')
		}).catch(() => {
			antd.message.error('복사에 실패했습니다.')
		})
	}

	const handleResendBill = async (billId: string) => {
		try {
			await resendBill(billId)
		} catch (err) {
			// Error already handled in hook
		}
	}

	const handleCancelBill = async (billId: string, price: number) => {
		try {
			await cancelBill(billId, price)
		} catch (err) {
			// Error already handled in hook
		}
	}

	const handleDestroyBill = async (billId: string, price: number) => {
		try {
			await destroyBill(billId, price)
		} catch (err) {
			// Error already handled in hook
		}
	}

	const getStatusBadge = (status: string) => {
		const statusMap: Record<string, { label: string; color: string }> = {
			W: { label: '결제 대기', color: '#faad14' },
			F: { label: '결제 완료', color: '#52c41a' },
			C: { label: '취소됨', color: '#ff4d4f' },
			D: { label: '파기됨', color: '#999999' },
		}
		const statusInfo = statusMap[status] || { label: status, color: '#999999' }
		return (
			<StatusBadge color={statusInfo.color}>
				{statusInfo.label}
			</StatusBadge>
		)
	}

	if (!deptId) {
		return (
			<PaymentWrapper>
				<EmptyState>지점 정보를 찾을 수 없습니다.</EmptyState>
			</PaymentWrapper>
		)
	}

	return (
		<PaymentWrapper>
			<LectureView>
				<LectureViewHeader>
					<p>결제 관리</p>
					{activeTab === 'history' && (
						<FixBtns>
							<button className="fix" onClick={fetchOrders} disabled={loading}>
								새로고침
							</button>
						</FixBtns>
					)}
				</LectureViewHeader>
				{merchantStatusChecked && !isMerchantApproved && (
					<MerchantWarning>
						결제선생 등록이 필요합니다. 내 학원 메뉴에서 가맹점 등록을 해주세요.
					</MerchantWarning>
				)}

				<antd.Tabs
					activeKey={activeTab}
					onChange={key => setActiveTab(key || 'bill')}
					size="middle"
					items={[
						{
							key: 'bill',
							label: '청구서 발송',
							children: (
						<BillSendLayout>
							<LeftPanel>
								<PanelTitle>수신자</PanelTitle>
								<antd.Segmented
									options={[
										{ label: '학생 선택', value: 'student' },
										{ label: '직접 입력', value: 'direct' },
									]}
									value={recipientMode}
									onChange={v => setRecipientMode(v as 'student' | 'direct')}
									style={{ marginBottom: 12 }}
								/>
								{recipientMode === 'student' && (
									<>
										<antd.Input.Search
											placeholder="이름/전화번호 검색"
											value={studentSearch}
											onChange={e => setStudentSearch(e.target.value)}
											allowClear
											style={{ marginBottom: 8 }}
										/>
										<SelectAllRow>
											<antd.Button type="link" size="small" onClick={toggleAllFiltered}>
												필터 전체 선택/해제
											</antd.Button>
										</SelectAllRow>
										<StudentListBox>
											{filteredStudents.map((s: StudentItem) => {
												const id = Number(s.id)
												const isSelected = selectedIds.includes(id)
												const parentTel = s?.phone2 ? String(s.phone2) : '-'
												const studentTel = s?.phone ? String(s.phone) : '-'
												return (
													<StudentRow
														key={id}
														$selected={isSelected}
														onClick={() => toggleOne(id)}
													>
														<CheckboxWrap onClick={e => e.stopPropagation()}>
															<antd.Checkbox
																checked={isSelected}
																onChange={() => toggleOne(id)}
															/>
														</CheckboxWrap>
														<div>
															<div className="name">{s?.name}</div>
															<div className="meta">학부모: {parentTel} / 학생: {studentTel}</div>
														</div>
													</StudentRow>
												)
											})}
											{filteredStudents.length === 0 && (
												<EmptyList>등록된 학생이 없거나 검색 결과가 없습니다.</EmptyList>
											)}
										</StudentListBox>
										<SelectionFooter>
											<span>선택: {selectedCount}명</span>
											<antd.Button type="link" size="small" onClick={() => setSelectedIds([])} disabled={selectedCount === 0}>
												선택 초기화
											</antd.Button>
										</SelectionFooter>
									</>
								)}
								{recipientMode === 'direct' && (
									<>
										<DirectEntryForm>
											<antd.Input
												placeholder="이름"
												value={directName}
												onChange={e => setDirectName(e.target.value)}
												onPressEnter={handleAddDirectRecipient}
											/>
											<antd.Input
												placeholder="전화번호"
												value={directPhone}
												onChange={e => setDirectPhone(e.target.value)}
												onPressEnter={handleAddDirectRecipient}
											/>
											<antd.Button type="primary" size="middle" onClick={handleAddDirectRecipient}>
												추가
											</antd.Button>
										</DirectEntryForm>
										<StudentListBox>
											{directRecipients.map((r, index) => (
												<DirectRecipientRow key={`${r.phone}-${index}`}>
													<div>
														<div className="name">{r.name}</div>
														<div className="meta">{r.phone}</div>
													</div>
													<antd.Button type="link" danger size="small" onClick={() => handleRemoveDirectRecipient(index)}>
														삭제
													</antd.Button>
												</DirectRecipientRow>
											))}
											{directRecipients.length === 0 && (
												<EmptyList>전화번호를 입력하고 추가 버튼을 눌러 수신자를 등록하세요.</EmptyList>
											)}
										</StudentListBox>
										<SelectionFooter>
											<span>수신: {directRecipients.length}명</span>
											<antd.Button type="link" size="small" onClick={() => setDirectRecipients([])} disabled={directRecipients.length === 0}>
												전체 삭제
											</antd.Button>
										</SelectionFooter>
									</>
								)}
							</LeftPanel>
							<RightPanel>
								<PanelTitle>발송 메시지</PanelTitle>
								{recipientMode === 'student' && (
									<antd.Radio.Group
										value={billRecipientPhone}
										onChange={e => setBillRecipientPhone(e.target.value)}
										style={{ marginBottom: 16 }}
									>
										<antd.Radio value="parent">학부모 번호로 발송</antd.Radio>
										<antd.Radio value="student">학생 번호로 발송</antd.Radio>
									</antd.Radio.Group>
								)}
								{recipientMode === 'student' && selectedCount > 0 && (
									<RecipientSummary>
										선택한 {selectedCount}명에게 동일한 청구서가 발송됩니다 ({billRecipientPhone === 'parent' ? '학부모' : '학생'} 번호 기준).
									</RecipientSummary>
								)}
								{recipientMode === 'direct' && directRecipients.length > 0 && (
									<RecipientSummary>
										직접 입력한 {directRecipients.length}명에게 동일한 청구서가 발송됩니다.
									</RecipientSummary>
								)}
								<BillForm>
									<FormRow>
										<FormLabel>상품명 *</FormLabel>
										<antd.Input
											value={productNm}
											onChange={e => setProductNm(e.target.value)}
											placeholder="상품명을 입력하세요"
											maxLength={100}
										/>
									</FormRow>
									<FormRow>
										<FormLabel>금액 *</FormLabel>
										<antd.Input
											type="number"
											value={amount}
											onChange={e => setAmount(e.target.value)}
											placeholder="20000"
											min={1}
										/>
										<FormHint>테스트: 20,000원 이상 권장</FormHint>
									</FormRow>
									<FormRow>
										<FormLabel>메시지</FormLabel>
										<antd.Input.TextArea
											value={message}
											onChange={e => setMessage(e.target.value)}
											placeholder="고객에게 전달할 메시지 (선택사항)"
											rows={3}
											maxLength={500}
										/>
									</FormRow>
									<FormRow>
										<FormLabel>만료일</FormLabel>
										<antd.Input
											type="date"
											value={expireDt}
											onChange={e => setExpireDt(e.target.value)}
											placeholder="YYYY-MM-DD"
										/>
									</FormRow>
									<FormRow>
										<FormLabel>청구서 발급처명</FormLabel>
										<antd.Input
											value={billIssuer}
											onChange={e => setBillIssuer(e.target.value)}
											placeholder={currentBranch?.name || '발급처명 (선택사항)'}
											maxLength={50}
										/>
									</FormRow>
										<antd.Button
											type="primary"
											onClick={handleSendBill}
											loading={sendingBill}
											disabled={!canSendBill || (recipientMode === 'student' ? selectedCount === 0 : directRecipients.length === 0)}
											size="large">
											청구서 발송
										</antd.Button>
									</BillForm>
							</RightPanel>
						</BillSendLayout>
							),
						},
						{
							key: 'history',
							label: '결제내역',
							children: (
						<>
						<OrdersTable>
							<thead>
								<tr>
									<th>청구서 ID</th>
									<th>고객명</th>
									<th>전화번호</th>
									<th>상품명</th>
									<th>금액</th>
									<th>상태</th>
									<th>발송일</th>
									<th>액션</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
											로딩 중...
										</td>
									</tr>
								) : orders.length === 0 ? (
									<tr>
										<td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
											주문 내역이 없습니다.
										</td>
									</tr>
								) : (
									orders.map((order) => (
										<tr key={order.id}>
											<td>{order.billId}</td>
											<td>{order.customerName}</td>
											<td>{order.customerPhone}</td>
											<td>{order.productNm}</td>
											<td>{order.amount.toLocaleString()}원</td>
											<td>{getStatusBadge(order.status)}</td>
											<td>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('ko-KR') : '-'}</td>
											<td>
												<ActionButtons>
													<ActionButton
														onClick={() => handleReadBill(order)}
														disabled={actionLoading[`read-${order.billId}`]}
													>
														조회
													</ActionButton>
													{order.status === 'W' && (
														<>
															<ActionButton
																onClick={() => handleResendBill(order.billId)}
																disabled={actionLoading[`resend-${order.billId}`]}
															>
																재전송
															</ActionButton>
															<ActionButton
																danger
																onClick={() => handleDestroyBill(order.billId, order.amount)}
																disabled={actionLoading[`destroy-${order.billId}`]}
															>
																파기
															</ActionButton>
														</>
													)}
													{order.status === 'F' && (
														<ActionButton
															danger
															onClick={() => handleCancelBill(order.billId, order.amount)}
															disabled={actionLoading[`cancel-${order.billId}`]}
														>
															취소
														</ActionButton>
													)}
												</ActionButtons>
											</td>
										</tr>
									))
								)}
							</tbody>
						</OrdersTable>
						{orders.some(o => o.shortUrl) && (
							<ShortUrlInfo>
								<p>전송된 청구서 링크를 확인하려면 해당내역의 조회 버튼을 클릭해주세요.</p>
							</ShortUrlInfo>
						)}
						</>
							),
						},
					]}
				/>
			</LectureView>

			<antd.Modal
				open={readModal.visible}
				title="청구서 조회"
				onCancel={() => setReadModal({ visible: false, order: null, data: null })}
				footer={[
					<antd.Button key="close" onClick={() => setReadModal({ visible: false, order: null, data: null })}>
						닫기
					</antd.Button>,
				]}
			>
				{readModal.order && (
					<ReadModalContent>
						<InfoRow>
							<InfoLabel>고객명</InfoLabel>
							<InfoValue>{readModal.order.customerName}</InfoValue>
						</InfoRow>
						<InfoRow>
							<InfoLabel>상품명</InfoLabel>
							<InfoValue>{readModal.order.productNm}</InfoValue>
						</InfoRow>
						<InfoRow>
							<InfoLabel>금액</InfoLabel>
							<InfoValue>{readModal.order.amount?.toLocaleString()}원</InfoValue>
						</InfoRow>
						<InfoRow>
							<InfoLabel>상태</InfoLabel>
							<InfoValue>{getStatusBadge(readModal.data?.appr_state || readModal.order.status)}</InfoValue>
						</InfoRow>
						{readModal.data?.appr_dt && (
							<InfoRow>
								<InfoLabel>승인일시</InfoLabel>
								<InfoValue>{readModal.data.appr_dt}</InfoValue>
							</InfoRow>
						)}
						{readModal.data?.appr_num && (
							<InfoRow>
								<InfoLabel>승인번호</InfoLabel>
								<InfoValue>{readModal.data.appr_num}</InfoValue>
							</InfoRow>
						)}
						{readModal.order.shortUrl && (
							<InfoRow>
								<InfoLabel>결제 링크</InfoLabel>
								<InfoValue>
									<ShortUrlRow>
										<a href={readModal.order.shortUrl} target="_blank" rel="noopener noreferrer">
											{readModal.order.shortUrl}
										</a>
										<antd.Button
											size="small"
											onClick={() => handleCopyUrl(readModal.order.shortUrl)}
										>
											복사
										</antd.Button>
									</ShortUrlRow>
								</InfoValue>
							</InfoRow>
						)}
					</ReadModalContent>
				)}
			</antd.Modal>
		</PaymentWrapper>
	)
}

const PaymentWrapper = styled(LectureWrapper)`
	padding: 30px 300px;
	@media (max-width: 1700px) {
		padding: 30px 220px;
	}
	@media (max-width: 1550px) {
		padding: 30px 130px;
	}
	@media (max-width: 1350px) {
		padding: 30px 80px;
	}
	@media (max-width: 1250px) {
		padding: 30px 50px;
	}
	@media (max-width: 1100px) {
		padding: 30px 20px;
	}
`

const BillSendLayout = styled.div`
	display: grid;
	grid-template-columns: 320px 1fr;
	gap: 24px;
	min-height: 480px;
	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`

const LeftPanel = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	background: #fff;
	padding: 16px;
	display: flex;
	flex-direction: column;
`

const RightPanel = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	background: #fff;
	padding: 20px;
	display: flex;
	flex-direction: column;
`

const PanelTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #333;
	margin-bottom: 12px;
`

const SelectAllRow = styled.div`
	margin-bottom: 8px;
`

const StudentListBox = styled.div`
	flex: 1;
	overflow-y: auto;
	max-height: 380px;
	border: 1px solid #f0f0f0;
	border-radius: 6px;
`

const CheckboxWrap = styled.span`
	display: inline-flex;
	margin-right: 10px;
	flex-shrink: 0;
`

const StudentRow = styled.div<{ $selected?: boolean }>`
	display: flex;
	align-items: center;
	padding: 12px;
	border-bottom: 1px solid #f5f5f5;
	cursor: pointer;
	transition: background 0.2s;
	background: ${p => (p.$selected ? '#e6f7ff' : 'transparent')};
	&:hover {
		background: ${p => (p.$selected ? '#bae7ff' : '#fafafa')};
	}
	&:last-child {
		border-bottom: none;
	}
	.name {
		font-weight: 600;
		color: #111;
		margin-bottom: 4px;
	}
	.meta {
		font-size: 12px;
		color: #666;
	}
`

const SelectionFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px solid #f0f0f0;
	font-size: 13px;
	color: #666;
`

const DirectEntryForm = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 12px;
	align-items: center;
	.ant-input {
		min-width: 80px;
	}
`

const DirectRecipientRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px;
	border-bottom: 1px solid #f5f5f5;
	background: transparent;
	transition: background 0.2s;
	&:hover {
		background: #fafafa;
	}
	&:last-child {
		border-bottom: none;
	}
	.name {
		font-weight: 600;
		color: #111;
		margin-bottom: 4px;
	}
	.meta {
		font-size: 12px;
		color: #666;
	}
`

const RecipientSummary = styled.div`
	font-size: 13px;
	color: #666;
	margin-bottom: 16px;
`

const EmptyList = styled.div`
	padding: 24px;
	text-align: center;
	color: #999;
	font-size: 14px;
`

const EmptyState = styled.div`
	width: 100%;
	height: 50vh;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	color: #666;
`

const MerchantWarning = styled.div`
	margin: 0 0 16px 0;
	padding: 12px 14px;
	border: 1px solid #ffe58f;
	background: #fffbe6;
	color: #ad6800;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
`

const OrdersTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-top: 20px;
	background-color: #fff;

	thead {
		background-color: #fafafa;
		th {
			padding: 12px;
			text-align: center;
			font-weight: 600;
			font-size: 14px;
			color: #333;
			border: 1px solid #e4e4e4;
		}
	}

	tbody {
		td {
			padding: 12px;
			text-align: center;
			font-size: 14px;
			color: #666;
			border: 1px solid #e4e4e4;
		}
	}
`

const StatusBadge = styled.span<{ color: string }>`
	display: inline-block;
	padding: 4px 8px;
	border-radius: 4px;
	background-color: ${props => props.color}20;
	color: ${props => props.color};
	font-size: 12px;
	font-weight: 500;
`

const ActionButtons = styled.div`
	display: flex;
	gap: 4px;
	justify-content: center;
	flex-wrap: wrap;
`

const ActionButton = styled.button<{ danger?: boolean }>`
	padding: 4px 8px;
	border-radius: 4px;
	border: 1px solid ${props => (props.danger ? '#ff4d4f' : '#1890ff')};
	background-color: #fff;
	color: ${props => (props.danger ? '#ff4d4f' : '#1890ff')};
	font-size: 12px;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background-color: ${props => (props.danger ? '#ff4d4f' : '#1890ff')};
		color: #fff;
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

const BillForm = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px 0;
`

const FormRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

const FormLabel = styled.label`
	font-weight: 500;
	font-size: 14px;
	color: #333;
`

const FormHint = styled.span`
	font-size: 12px;
	color: #999;
	margin-top: -4px;
`

const ReadModalContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 8px 0;
`

const InfoRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
`

const InfoLabel = styled.span`
	min-width: 80px;
	font-weight: 600;
	font-size: 14px;
	color: #555;
	flex-shrink: 0;
`

const InfoValue = styled.span`
	font-size: 14px;
	color: #222;
	word-break: break-all;
`

const ShortUrlRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	a {
		color: #1890ff;
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}
`

const ShortUrlInfo = styled.div`
	margin-top: 20px;
	padding: 12px;
	background-color: #e6f7ff;
	border: 1px solid #91d5ff;
	border-radius: 4px;
	p {
		margin: 0;
		font-size: 14px;
		color: #0050b3;
	}
`

export default React.memo(Payment)
